import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useAmplitude } from 'hooks/useAmplitude';
import { lendingApiFetchJson } from 'lendingApi/lendingApi';
import { TDeserialisedIntroducerFormData } from 'types';
import { buildAddressLabel } from 'utils/addressBuilder';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { URLS } from 'utils/tests/urls.config';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';

async function submitIntroducerSignup(values: TDeserialisedIntroducerFormData) {
  let data = serialiseIntroducerStatePost(values);
  let uuid = values.uuid;
  if (!uuid) {
    uuid = (
      await lendingApiFetchJson(URLS.postIntroducerAccount(), {
        method: 'post',
        body: JSON.stringify({ data }),
      })
    ).data.introducer_account_id;
  }
  data = serialiseIntroducerStatePost({ ...values, uuid });
  await lendingApiFetchJson(URLS.postIntroducer(), {
    method: 'post',
    body: JSON.stringify({ data }),
  });
}

const serialiseIntroducerStatePost = function (
  values: TDeserialisedIntroducerFormData,
) {
  if (values.uuid) {
    return {
      title: values.title,
      first_name: values.firstName,
      last_name: values.lastName,
      email: values.email,
      phone_number: values.phoneNumber,
      introducer_company_id: values.uuid,
    };
  }
  return {
    introducer_account: {
      introducer_type: values.introducerType,
      address: values.registeredTradingAddress,
      company_name: values.companyName,
      company_number: values.companyNumber,
      email: values.email,
      fca_reference: values.FCAReferenceNumber,
      phone_number: values.phoneNumber,
      post_code: values.postcode,
      vat_number: values.vatNumber,
      website: values.companyWebsite,
    },
  };
};

export const generateUKAddress = (
  values: Omit<TDeserialisedIntroducerFormData, 'registeredTradingAddress'>,
) => {
  return buildAddressLabel([
    values.addressLine1,
    values.addressLine2,
    values.town,
  ]);
};

const generateDEAddress = (
  values: Omit<TDeserialisedIntroducerFormData, 'registeredTradingAddress'>,
) => {
  const address = `${values.addressLine1} ${values.addressLine2}, ${values.town}`;
  return address;
};

export const useRegisterIntroducerMutation = ({
  onError,
  country,
}: {
  onError: () => void;
  country: 'UK' | 'Germany';
}) => {
  const { trackEvent } = useAmplitude();
  const router = useRouter();
  const inviteId = router.query.introducer as string | undefined;

  const signUpMutation = useMutation({
    mutationFn: (
      values: Omit<TDeserialisedIntroducerFormData, 'registeredTradingAddress'>,
    ) => {
      const registeredTradingAddress =
        country === 'Germany'
          ? generateDEAddress(values)
          : generateUKAddress(values);

      return submitIntroducerSignup({
        ...values,
        registeredTradingAddress,
        uuid: inviteId,
      });
    },
    onSuccess: (data, { introducerType }) => {
      trackEvent(AMPLITUDE_EVENTS.introducerRegisterSubmitted, {
        'Introducer type': introducerType,
        'Invite link': inviteId as string,
      });

      router.push(PATH_NAMES.home());
    },
    onError: () => {
      onError();
    },
  });

  return signUpMutation;
};
